import { Component, Input, OnInit } from '@angular/core'
import { IKeyword } from '../../../graphql/queries/keyword.queries'
import moment from 'moment'
import 'moment-timezone'

@Component({
  selector: 'request-details',
  template: `
    <div *ngIf="keyword.deadline" class="mr-2 text-md-right">
      <div [popover]="deadlinePopover"
        triggers="mouseenter:mouseleave"
        container="body"
        [class.text-danger]="deadlinePassed(keyword.deadline)">
        {{shortDeadlineString(keyword.deadline, keyword.timeZone)}}
      </div>
      <ng-template #deadlinePopover>
        <div class="text-one-line" style="min-width: 100%;">
          <span *ngIf="deadlinePassed(keyword.deadline)">Past due</span>
          <span *ngIf="!deadlinePassed(keyword.deadline)">
            {{longDeadlineString(keyword.deadline, keyword.timeZone)}} {{shortTimeZoneFormat(keyword.timeZone)}}
          </span>
        </div>
      </ng-template>
    </div>
    <div *ngIf="activatedAtVisible" [popover]="activatedAtPopoverText" triggers="mouseenter:mouseleave">
      <i class="text-muted far fa-clock"></i>
    </div>
    <div *ngIf="keyword.writer && keyword.state !== 'declined'" class="text-md-right">
      <writer-avatar
        [writer]="keyword.writer"
        [customerId]="keyword.customerId"
        [size]="20"
        [hideName]="true"
        [transparentAvatar]="avatarIsTransparent(keyword)"
        borderRadius="50%"
        popover="{{getPopoverText(keyword)}}"
        triggers="mouseenter:mouseleave"
        container="body"></writer-avatar>
    </div>
    <div *ngIf="keyword.previousWriter && keyword.state === 'declined'" class="text-md-right">
      <writer-avatar
        [writer]="keyword.previousWriter"
        [customerId]="keyword.customerId"
        [size]="20"
        [hideName]="true"
        [showBorder]="true"
        borderRadius="50%"
        popover="{{getPopoverText(keyword)}}"
        triggers="mouseenter:mouseleave"
        container="body"></writer-avatar>
    </div>
    <div *ngIf="isOpenKeywordAndHasDrafts">
      <ng-template #keywordWithDraftsPopover><div [innerHtml]="popoverText"></div></ng-template>
      <div [popover]="keywordWithDraftsPopover" triggers="mouseenter:mouseleave">
        <img class="rounded-circle" alt='user' src="/assets/user-light-bg.png" width="20">
      </div>
    </div>
    <div *ngIf="keyword.postLimit > 1" class="ml-2 text-md-right">
      <i class="text-muted fas fa-sync-alt"></i>
    </div>
    <div *ngIf="keyword.documents.length > 0" popover="This request contains attachments" triggers="mouseenter:mouseleave">
      <i class="text-muted fas fa-paperclip ml-2"></i>
    </div>
  `
})
export class RequestDetails implements OnInit {

  @Input() public contentTypesEnabled: boolean;
  @Input() public keyword: IKeyword;

  popoverText: string = ""
  isOpenKeywordAndHasDrafts: boolean = false;
  ngOnInit(): void {
    this.isOpenKeywordAndHasDrafts = this.isOpenKeyword && this.keywordHasDrafts
    if (this.isOpenKeywordAndHasDrafts) { this.popoverText = this.getActiveDraftPopoverText(this.keyword) }
  }

  get activatedAtPopoverText(): string {
    let text = "You activated this request on "
    const activatedAt = moment(this.keyword.activatedAt || this.keyword.createdAt)
    const notThisYear = activatedAt.year() < moment().year()
    if (notThisYear) {
      return text + activatedAt.format('dddd, MMMM  Do, YYYY.')
    } else {
      return text + activatedAt.format('dddd, MMMM  Do.')
    }
  }

  get isOpenKeyword(): boolean {
    return this.keyword.kind === 'open'
  }

  get keywordHasDrafts():boolean {
    return !!this.keyword.firstDraftStartedAt
  }

  get isActive(){
    return this.keyword.state == "active"
  }

  get activatedAtVisible(): boolean {
    return this.isOpenKeyword && this.isActive && !this.keywordHasDrafts
  }

  deadlinePassed(deadline: Date): boolean {
    return moment().isAfter(moment(deadline))
  }

  hasSubmissions(keyword: IKeyword): boolean {
    return (keyword.postedCount > 0 || keyword.queuedCount > 0)
  }

  avatarIsTransparent(keyword: IKeyword): boolean {
    return keyword.kind === 'assigned' && keyword.state !== 'active'
  }

  getPopoverText(keyword: IKeyword): string {
    if (keyword.kind === 'deadline') {
      return keyword.writer.displayName + ' has claimed this request.'
    } else if (keyword.kind === 'assigned') {
      if (keyword.state === 'active') {
        return keyword.writer.displayName + ' has accepted this assignment.'
      } else if (keyword.state === 'assigned') {
        return keyword.writer.displayName + ' has 24 hours to accept this assignment.'
      } else if (keyword.state === 'declined') {
        return keyword.previousWriter.displayName + ' has rejected this request.'
      }
    }
  }

  // z: either MST, +11, +0530 depending on what's available
  shortTimeZoneFormat(timeZoneId: string): string {
    return timeZoneId ? moment.tz(timeZoneId).format('z') : ''
  }

  longDeadlineString(deadline: Date, timeZone: string): string {
    return moment.tz(deadline, timeZone).format('ddd, MMM Do, YYYY, h a')
  }

  shortDeadlineString(deadline: Date, timeZone: string): string {
    return moment.tz(deadline, timeZone).format('MMM D')
  }

  getActiveDraftPopoverText(keyword: IKeyword): string {
    const bonusDate = moment.tz(keyword.firstDraftStartedAt, keyword.timeZone).add(3, 'days').format('MM/DD')
    let draftMessage = `A writer has started a draft. If content isn't submitted by ${bonusDate}, reach out to support@verblio.com`
    return `<p>${draftMessage}.</p> ${this.activatedAtPopoverText}`
  }

}
